import type { PayloadAction } from '@reduxjs/toolkit';

import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit';

import { RootState } from '@app/store';
import { FEEDBACK_SUCCESS_TEXT, FEEDBACK_SUCCESS_TITLE } from '@shared/constants/text';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';
import { sendFeedbackForm } from '@widgets/Feedback/api/sendFeedbackForm/sendFeedbackForm';
import { FeedbackErrorsType, FeedbackStateType } from '@widgets/Feedback/model/types/feedbackState';

import { openAlert } from '../../../AlertModal';

const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator }
});

const initialState: FeedbackStateType = {
  name: '',
  phone: '',
  success: false,
  loading: false,
  agree: false,
  errors: {
    phone: false,
    name: false,
    agree: false
  }
};

export const feedbackSlice = createAppSlice({
  name: 'feedback',
  initialState,
  reducers: (create) => ({
    changeName: create.reducer((state, action: PayloadAction<string>) => {
      state.name = action.payload;
      state.errors.name = false;
    }),
    changePhone: create.reducer((state, action: PayloadAction<string>) => {
      state.phone = action.payload;
      state.errors.phone = false;
    }),
    changeAgree: create.reducer((state, action: PayloadAction<boolean>) => {
      state.agree = action.payload;
      state.errors.agree = false;
    }),
    updateErrors: create.reducer((state, action: PayloadAction<FeedbackErrorsType>) => {
      state.errors = action.payload;
    }),
    sendData: create.asyncThunk(
      async (_: undefined, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const { name, phone, agree } = store.feedback;

        let isValid = true;

        const errors: FeedbackErrorsType = {
          name: false,
          phone: false,
          agree: false
        };

        if (!name) {
          errors.name = true;
          isValid = false;
        }

        if (!phone) {
          errors.phone = true;
          isValid = false;
        }

        if (!agree) {
          errors.agree = true;
          isValid = false;
        }
        if (!isValid) {
          thunkAPI.dispatch(feedbackSlice.actions.updateErrors(errors));
          return thunkAPI.rejectWithValue(errors);
        }

        const data = await sendFeedbackForm({ name, phone });

        if (data) {
          thunkAPI.dispatch(
            openAlert({
              data: {
                title: FEEDBACK_SUCCESS_TITLE,
                text: FEEDBACK_SUCCESS_TEXT
              }
            })
          );
        }

        return {
          data
        };
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.success = true;
          state.name = '';
          state.phone = '';
          state.agree = false;

          state.errors = initialState.errors;

          sendMetrics(Metrics.CALL_ME_FORM);
        },
        settled: (state) => {
          state.loading = false;
        }
      }
    )
  })
});

export const { changeName, changePhone, changeAgree, sendData, updateErrors } = feedbackSlice.actions;

export const feedbackReducer = feedbackSlice.reducer;
